import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { VisiteTrattenute, riparti } from "../lib/visite_trattenute"
import { formatApiErrorDetail } from "../lib/api_errore"

// L'assistente delle voci di spesa della scheda «Preparazione domanda» (components/readiness/ApplicationPrep.js:190-276 e
// 794-904, 31a5bd1), su readiness/app_prep/_voci_assistant. Lo stato di React (vociOpen, vociLoading, suggestions,
// addingVoci, postAdd, dmSim) è qui; le righe si disegnano dai modelli <template> del partial.
// - dialog:opened (openVociAssistant): attesa, POST /api/projects/:id/suggest-voci { grant_id }; ogni proposta con un id
//   `v_…` e non scelta; nessuna: toast.info; errore: toast.error del dettaglio e la finestra chiusa.
// - la scelta (toggleSuggestion) e l'importo solo in cifre (updateSuggAmount: 0 è il campo vuoto); il conteggio e il
//   totale delle scelte.
// - bando de minimis (simKey): 400 ms dopo l'ultima modifica della scelta o di un importo, POST …/de-minimis/simula con
//   le voci come saranno salvate (vociWith); un errore toglie il riquadro. Come l'effetto di React la simulazione parte
//   solo quando la scelta cambia (non all'arrivo delle proposte), mai nel riepilogo né per chi legge soltanto, e il
//   risultato precedente resta finché non arriva il nuovo.
// - «Aggiungi selezionate» (addSelectedVoci): almeno una voce, ognuna con l'importo; PUT /api/projects/:id/voci con
//   vociWith (la voce creata da EROGO dal budget ridotta di quanto si aggiunge, tolta se finisce); il toast, il
//   riepilogo (buildRecap, il de minimis della risposta) e la pagina riletta (onVociUpdated: refresh con morph; la
//   finestra resta aperta perché il suo morph si annulla finché è aperta). Durante il PUT le visite Turbo aspettano la
//   risposta (lib/visite_trattenute.js), poi l'ultima riparte (alla stessa pagina con action "replace").
// - «Rigenera bozze» del riepilogo: la finestra si chiude e parte «Prepara la domanda» del pannello (run, con la sua
//   conferma).
// Le richieste in corso si annullano alla chiusura (le proposte) e all'uscita dalla pagina (proposte e simulazione).
const ATTESA_SIMULA = 400
const DA_CLASSIFICARE = "da_classificare"
const uid = () => `v_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
// (Number(n) || 0).toLocaleString("it-IT")
const eur = (n) => (Number(n) || 0).toLocaleString("it-IT")

// formatEuro di lib/api.js
function eurc(n) {
  const v = Number(n || 0)
  const [int, dec] = Math.abs(v).toFixed(2).split(".")
  return `${v < 0 && Number(Math.abs(v).toFixed(2)) !== 0 ? "-" : ""}${int.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${dec} €`
}

async function richiesta(url, { method, body, signal }) {
  const token = document.querySelector('meta[name="csrf-token"]')?.content
  const res = await fetch(url, {
    method, signal, credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json", ...(token ? { "X-CSRF-Token": token } : {}) },
    body: JSON.stringify(body),
  })
  let data
  try { data = await res.json() } catch { data = undefined }
  if (!res.ok) {
    const errore = new Error(`HTTP ${res.status}`)
    errore.detail = data?.detail
    throw errore
  }
  return data
}

export default class extends Controller {
  static targets = ["titolo", "recap", "totale", "perBando", "recapDm", "recapBarra", "scelta", "plafond", "plafondBarra", "stime",
    "attesa", "elenco", "lista", "nessuna", "conteggio", "aggiungi", "aggiungiAttesa", "aggiungiIcona",
    "modelloVoce", "modelloBando", "modelloBarra"]
  static values = { dati: Object }

  initialize() {
    this.suggestions = []
    this.postAdd = null
    this.dmSim = null
    this.aperta = false
    this.chiaveSim = null
  }

  connect() {
    this.visite = new VisiteTrattenute()
  }

  disconnect() {
    this.visite.rilascia()
    this.proposte?.abort()
    this.simulazione?.abort()
    clearTimeout(this.timerSim)
  }

  get dati() { return this.datiValue }
  get base() { return `/api/projects/${encodeURIComponent(this.dati.progetto)}` }
  get finestra() { return this.element.querySelector("#dialog-voci-assistant") }

  // finché la finestra è aperta la pagina riletta non la tocca (lo stato di React restava montato)
  conserva(event) {
    if (event.target === this.element && this.aperta) event.preventDefault()
  }

  // --- apertura e chiusura -----------------------------------------------------------------------------------------

  async apri(event) {
    if (event.target !== this.finestra) return
    this.aperta = true
    this.postAdd = null
    this.suggestions = []
    this.caricando = true
    this.disegna()
    // il fuoco nella finestra: con l'attesa non c'è un campo, resta la X (il primo elemento attivabile di Radix)
    setTimeout(() => {
      const contenuto = this.finestra.querySelector("[role='dialog']")
      // la X di ui_dialog è l'unico pulsante di chiusura figlio diretto del contenuto
      if (!contenuto.contains(document.activeElement)) contenuto.querySelector(":scope > button[data-action='dialog#close']")?.focus()
    }, 0)
    this.proposte?.abort()
    const ctrl = (this.proposte = new AbortController())
    try {
      const data = await richiesta(`${this.base}/suggest-voci`, { method: "POST", body: this.dati.grant_id ? { grant_id: this.dati.grant_id } : {}, signal: ctrl.signal })
      if (ctrl !== this.proposte) return
      this.suggestions = (data?.suggestions || []).map((s) => ({ ...s, id: uid(), selected: false }))
      if (!this.suggestions.length) window.toast?.info?.("Nessun suggerimento disponibile per ora.")
    } catch (e) {
      if (ctrl !== this.proposte || e.name === "AbortError") return
      window.toast?.error?.(formatApiErrorDetail(e.detail) || "Suggerimenti non disponibili.")
      this.chiudi()
    } finally {
      if (ctrl === this.proposte) {
        this.proposte = null
        this.caricando = false
        this.disegna()
      }
    }
  }

  chiuso(event) {
    if (event.target !== this.finestra) return
    this.aperta = false
    this.postAdd = null
    this.proposte?.abort()
    this.proposte = null
    clearTimeout(this.timerSim)
  }

  chiudi() {
    window.dispatchEvent(new CustomEvent("dialog:close", { detail: { id: "voci-assistant" } }))
  }

  // closeVociDialog(); run(): «Prepara la domanda» del pannello, con la stessa conferma della pratica
  rigenera() {
    this.chiudi()
    const pulsante = this.element.closest("#application-prep-panel")?.querySelector("[data-testid='run-prepare-application-btn']")
    pulsante?.form?.requestSubmit(pulsante)
  }

  // --- scelta e importi --------------------------------------------------------------------------------------------

  // le righe restano le stesse (come gli elementi di React): il fuoco resta sul pulsante della scelta
  scegli(i) {
    this.suggestions = this.suggestions.map((x, k) => (k === i ? { ...x, selected: !x.selected } : x))
    this.disegna({ righe: false })
  }

  importo(i, input) {
    const n = Number(String(input.value).replace(/[^0-9]/g, "")) || 0
    this.suggestions = this.suggestions.map((x, k) => (k === i ? { ...x, importo: n } : x))
    const testo = n ? String(n) : ""
    if (input.value !== testo) input.value = testo
    this.disegna({ righe: false })
  }

  // vociWith: la voce creata da EROGO dal budget lascia il posto alle voci vere, ridotta di quanto si aggiunge
  vociWith(chosen) {
    const added = chosen.reduce((a, s) => a + (Number(s.importo) || 0), 0)
    let reduced = 0
    const existing = (this.dati.voci || []).flatMap((v) => {
      if (!this.automatica(v)) return [v]
      const left = Math.max(0, (Number(v.importo) || 0) - added)
      reduced = (Number(v.importo) || 0) - left
      return left > 0 ? [{ ...v, importo: left }] : []
    })
    const grantId = this.dati.grant_id || null
    return { reduced, voci: [...existing, ...chosen.map((s) => ({ id: s.id || uid(), nome: s.nome, importo: Number(s.importo) || 0, categoria: s.categoria, bandoId: grantId, origine: s.origine || undefined }))] }
  }

  // isVoceAutomatica di lib/bandoMeta.js
  automatica(v) {
    if (v.automatica !== undefined && v.automatica !== null) return !!v.automatica
    return this.dati.segnaposto.includes(v.nome) && !v.bandoId && ["software", DA_CLASSIFICARE].includes(v.categoria)
  }

  // spesaLabel di lib/bandoMeta.js
  spesaLabel(key) {
    if (!key) return ""
    if (key === DA_CLASSIFICARE) return "Da classificare"
    const c = this.dati.categorie.find((x) => x.key === (this.dati.scenario_vecchio[key] || key))
    if (c) return c.label
    return this.dati.vecchie[key] || key
  }

  // --- de minimis con le voci scelte -------------------------------------------------------------------------------

  pianificaSimulazione() {
    if (!this.dati.de_minimis) return
    const chiave = JSON.stringify(this.suggestions.filter((s) => s.selected).map((s) => [s.id, s.importo]))
    if (chiave === this.chiaveSim) return
    this.chiaveSim = chiave
    clearTimeout(this.timerSim)
    if (!this.aperta || !this.suggestions.length || this.postAdd || !this.dati.can_edit) return
    this.timerSim = setTimeout(() => this.simula(), ATTESA_SIMULA)
  }

  async simula() {
    this.simulazione?.abort()
    const ctrl = (this.simulazione = new AbortController())
    try {
      const data = await richiesta(`${this.base}/de-minimis/simula`, { method: "POST", body: { voci: this.vociWith(this.suggestions.filter((s) => s.selected)).voci }, signal: ctrl.signal })
      if (ctrl !== this.simulazione) return
      this.dmSim = data
    } catch (e) {
      if (ctrl !== this.simulazione || e.name === "AbortError") return
      this.dmSim = null
    }
    this.simulazione = null
    this.disegnaPlafond()
  }

  // --- aggiunta ----------------------------------------------------------------------------------------------------

  async aggiungi() {
    const chosen = this.suggestions.filter((s) => s.selected)
    if (!chosen.length) { window.toast?.info?.("Seleziona almeno una voce."); return }
    if (chosen.some((s) => !(Number(s.importo) > 0))) { window.toast?.info?.("Scrivi l'importo delle voci scelte: l'AI non propone importi."); return }
    this.salvando = true
    this.visite.trattieni()
    this.disegnaPulsante()
    let fatto = false
    try {
      const { reduced, voci } = this.vociWith(chosen)
      const saved = await richiesta(`${this.base}/voci`, { method: "PUT", body: { voci } })
      window.toast?.success?.(`${chosen.length} voci aggiunte al progetto.` +
        (reduced ? ` La voce creata da EROGO dal budget è stata ridotta di ${eur(reduced)} €.` : ""))
      this.postAdd = { ...this.recap(voci), dm: saved?.de_minimis || null }
      // postAdd cambiato: l'effetto della simulazione si ferma (cleanup del timer)
      clearTimeout(this.timerSim)
      this.dati = { ...this.dati, voci: saved?.voci || voci }
      fatto = true
    } catch {
      window.toast?.error?.("Aggiunta voci non riuscita.")
    } finally {
      this.salvando = false
      this.disegna()
    }
    // onVociUpdated (loadProject): la pagina riletta; una visita chiesta durante il salvataggio riparte
    const visita = this.visite.rilascia()
    if (visita) riparti(visita)
    // senza salvare la pagina nella cache (turbo:before-cache chiuderebbe la finestra: dialog_controller#closeNow)
    else if (fatto) Turbo.session.refresh(document.baseURI, { method: "morph", scroll: "preserve" })
  }

  set dati(v) { this.datiValue = v }

  // buildRecap: il totale e la ripartizione per bando, dal più grande
  recap(voci) {
    const total = voci.reduce((a, v) => a + (Number(v.importo) || 0), 0)
    const map = new Map()
    for (const v of voci) {
      const key = v.bandoId || "__none__"
      const cur = map.get(key) || { total: 0, count: 0 }
      cur.total += Number(v.importo) || 0
      cur.count += 1
      map.set(key, cur)
    }
    const perBando = [...map.entries()].map(([key, val]) => {
      const g = key === "__none__" ? null : this.dati.bandi[key]
      return { key, name: key === "__none__" ? "Non assegnate a un bando" : (g?.name || "Bando"), deMinimis: !!g?.de_minimis, ...val }
    }).sort((a, b) => b.total - a.total)
    return { total, perBando }
  }

  // --- disegno -----------------------------------------------------------------------------------------------------

  disegna({ righe = true } = {}) {
    const recap = !!this.postAdd
    this.titoloTarget.textContent = recap ? "Voci aggiunte — riepilogo budget" : "Voci di spesa suggerite"
    this.recapTarget.hidden = !recap
    this.mostra(this.sceltaTarget, !recap)
    if (recap) this.disegnaRecap()
    this.attesaTarget.hidden = !this.caricando
    this.mostra(this.elencoTarget, !this.caricando)
    if (righe) this.disegnaRighe()
    else this.aggiornaRighe()
    const scelte = this.suggestions.filter((s) => s.selected)
    const totale = scelte.reduce((a, s) => a + (Number(s.importo) || 0), 0)
    this.conteggioTarget.textContent = `${scelte.length} selezionate · ${eur(totale)} €`
    this.disegnaPulsante()
    this.disegnaPlafond()
    this.pianificaSimulazione()
  }

  // un contenitore senza box (contents): `hidden` non vale contro la classe, si scambiano le classi
  mostra(el, si) {
    el.classList.toggle("contents", si)
    el.classList.toggle("hidden", !si)
  }

  disegnaPulsante() {
    const btn = this.aggiungiTarget
    btn.disabled = !!this.salvando || !this.dati.can_edit || !this.suggestions.some((s) => s.selected)
    this.aggiungiAttesaTarget.toggleAttribute("hidden", !this.salvando)
    this.aggiungiIconaTarget.toggleAttribute("hidden", !!this.salvando)
  }

  disegnaRighe() {
    this.listaTarget.querySelectorAll("[data-parte='riga']").forEach((el) => el.remove())
    this.nessunaTarget.hidden = this.suggestions.length > 0
    this.suggestions.forEach((s, i) => {
      const riga = this.modelloVoceTarget.content.firstElementChild.cloneNode(true)
      riga.dataset.testid = `voce-suggestion-${i}`
      const q = (p) => riga.querySelector(`[data-parte="${p}"]`)
      const scelta = q("scelta")
      scelta.dataset.testid = `voce-toggle-${i}`
      scelta.addEventListener("click", () => this.scegli(i))
      q("nome").textContent = s.nome ?? ""
      const input = q("importo")
      input.dataset.testid = `voce-importo-${i}`
      input.value = s.importo ? String(s.importo) : ""
      input.addEventListener("input", () => this.importo(i, input))
      q("descrizione").textContent = s.descrizione || ""
      q("descrizione").hidden = !s.descrizione
      q("categoria").textContent = this.spesaLabel(s.categoria)
      q("categoria-bando").textContent = s.categoria_bando ? `Categoria del bando: ${s.categoria_bando}` : ""
      q("categoria-bando").hidden = !s.categoria_bando
      this.listaTarget.append(riga)
    })
    this.aggiornaRighe()
  }

  aggiornaRighe() {
    this.listaTarget.querySelectorAll("[data-parte='riga']").forEach((riga, i) => {
      const s = this.suggestions[i]
      const q = (p) => riga.querySelector(`[data-parte="${p}"]`)
      riga.classList.toggle("border-violet-300", s.selected)
      riga.classList.toggle("bg-violet-50/60", s.selected)
      riga.classList.toggle("border-amber-300", !s.selected)
      riga.classList.toggle("bg-[#fff6cc]", !s.selected)
      const scelta = q("scelta")
      scelta.classList.toggle("bg-violet-600", s.selected)
      scelta.classList.toggle("border-violet-600", s.selected)
      scelta.classList.toggle("border-zinc-300", !s.selected)
      scelta.setAttribute("aria-pressed", String(!!s.selected))
      q("spunta").toggleAttribute("hidden", !s.selected)
      q("proposta").hidden = !(s.origine === "proposta_ai" && !s.selected)
    })
  }

  disegnaPlafond() {
    const dm = this.dmSim
    const si = this.dati.de_minimis && !this.caricando && this.suggestions.length > 0 && !!dm && !this.postAdd
    this.plafondTarget.hidden = !si
    if (!si) return
    const oltre = !!dm.oltre
    this.plafondTarget.classList.toggle("border-rose-300", oltre)
    this.plafondTarget.classList.toggle("bg-rose-50", oltre)
    this.plafondTarget.classList.toggle("border-amber-200", !oltre)
    this.plafondTarget.classList.toggle("bg-amber-50/60", !oltre)
    this.plafondBarraTarget.replaceChildren(this.barra(dm, "deminimis-sim"))
    const stime = dm.modalita === "stime"
    this.stimeTarget.hidden = !stime
    if (stime) {
      const d = dm.analisi_del
      this.stimeTarget.textContent = `Ci sono voci da classificare: il conteggio usa le stime dell'analisi${d ? ` del ${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : " salvata"}, non il calcolo di oggi.`
    }
  }

  disegnaRecap() {
    const r = this.postAdd
    this.totaleTarget.textContent = `${eur(r.total)} €`
    this.perBandoTarget.querySelectorAll("[data-testid^='recap-bando-']").forEach((el) => el.remove())
    r.perBando.forEach((b) => {
      const riga = this.modelloBandoTarget.content.firstElementChild.cloneNode(true)
      riga.dataset.testid = `recap-bando-${b.key}`
      riga.querySelector("[data-parte='nome']").textContent = b.name ?? ""
      riga.querySelector("[data-parte='conteggio']").textContent = `${b.count} voci${b.deMinimis ? " · de minimis" : ""}`
      riga.querySelector("[data-parte='totale']").textContent = `${eur(b.total)} €`
      this.perBandoTarget.append(riga)
    })
    const dm = r.dm
    const si = !!dm && Array.isArray(dm.righe) && dm.righe.length > 0
    this.recapDmTarget.hidden = !si
    this.recapBarraTarget.replaceChildren(...(si ? [this.barra(dm, "recap-deminimis")] : []))
  }

  // DeMinimisBar: il totale sul tetto, la barra (rossa oltre il tetto, ambra oltre l'80%), l'eccedenza o il residuo
  barra(dm, testid) {
    const el = this.modelloBarraTarget.content.firstElementChild.cloneNode(true)
    const q = (p) => el.querySelector(`[data-parte="${p}"]`)
    const pct = Math.min(100, (Number(dm.totale) / Number(dm.tetto)) * 100)
    const oltre = !!dm.oltre
    const totale = q("totale")
    totale.textContent = `${eurc(dm.totale)} su ${eurc(dm.tetto)}`
    totale.dataset.testid = `${testid}-totale`
    const barra = q("barra")
    barra.classList.add(oltre ? "bg-rose-500" : pct > 80 ? "bg-amber-500" : "bg-emerald-500")
    if (!Number.isNaN(pct)) barra.style.width = `${pct}%`
    if (oltre) {
      q("oltre").hidden = false
      q("oltre").dataset.testid = `${testid}-oltre`
      q("eccedenza").textContent = eurc(dm.eccedenza)
      q("oltre-totale").textContent = eurc(dm.totale)
      q("oltre-tetto").textContent = eurc(dm.tetto)
    } else {
      q("residuo").hidden = false
      q("residuo").dataset.testid = `${testid}-residuo`
      q("residuo-valore").textContent = eurc(dm.residuo)
    }
    return el
  }
}
